import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {ReelConstants, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {ScatterReplacements, Win} from '@fantasmagames/slot/src/logic/integration/RecodeLogic';
import {
	AvalancheNewSymbol,
	ExpandingWildReel,
	GameWinLine,
	RespinScatterInfo,
	SpinResult,
} from '@fantasmagames/slot/src/logic/SpinResult';

export class RecodeSpinResult extends SpinResult {

	public static readonly PAYLINES: number[][] = [
		[1, 1, 1, 1, 1 ],
		[0, 0, 0, 0, 0 ],
		[2, 2, 2, 2, 2 ],
		[0, 1, 2, 1, 0 ],
		[2, 1, 0, 1, 2 ],
		[0, 0, 1, 0, 0 ],
		[2, 2, 1, 2, 2 ],
		[1, 2, 2, 2, 1 ],
		[1, 0, 0, 0, 1 ],
		[1, 0, 1, 0, 1 ],
		[1, 2, 1, 2, 1 ],
		[0, 1, 0, 1, 0 ],
		[2, 1, 2, 1, 2 ],
		[1, 1, 0, 1, 1 ],
		[1, 1, 2, 1, 1 ],
		[2, 1, 1, 1, 2 ],
		[0, 1, 1, 1, 0 ],
		[2, 0, 1, 0, 2 ],
		[0, 2, 1, 2, 0 ],
		[2, 0, 2, 0, 2 ],
	];

	public cleanup(): void {
		this._winLines = [];
	}

	public setReels(symbols: number[], symbolHeights: number[]): void {
		this._reels = this.makeGameReelsFromRecodeReels(symbols, symbolHeights);
	}

	public setPreviousReels(symbols: number[], symbolHeights: number[]): void {
		this._previousReels = this.makeGameReelsFromRecodeReels(symbols, symbolHeights);
	}

	public makeGameReelsFromRecodeReels(symbols: number[], symbolHeights: number[]): GameSymbol[][] {
		const reels: GameSymbol[][] = [];

		let symbolIndex: number = 0;
		let reelIndex: number = 0;

		for (let symIndex = 0; symIndex < symbols.length; symIndex++) {
			const symbol = symbols[symIndex];

			// for (const symbol of symbols) {
			if (!reels[reelIndex]) {
				reels.push([]);
			}

			const gameSymbol: GameSymbol = this.makeGameSymbolFrom(symbol, symbolHeights[symIndex], {
				x: reelIndex,
				y: symbolIndex,
			});
			if (gameSymbol) {
				reels[reelIndex].push(gameSymbol);
			}
			reelIndex++;
			if (reelIndex > ReelConstants.REELS_AMOUNT - 1) {
				symbolIndex++;
				reelIndex = 0;
			}
		}

		return reels;
	}

	public setWaysToWin(wtw: number): void {
		this._waysToWin = wtw;
	}

	public setTotalWin(win: number): void {
		this._win = win;
	}

	public setRoundWin(win: number): void {
		this._spinWin = win;
	}

	public setNextRoundWin(win: number): void {
		this._nextSpinWin = win;
	}

	public setScatters(currentScatters: ScatterReplacements): void {
		this._respinScatters = [];

		let respinSymbols: SymbolPosition[] = [];
		let currentScatter: number = 0;
		const symsInScatter: number = Math.floor(currentScatters.symbolsPositions.length / currentScatters.scattersPositions.length);
		const currentSymbols: number[] = [...currentScatters.symbolsPositions];

		while (currentSymbols.length > 0) {
			const sym: number = currentSymbols.pop();
			respinSymbols.push(this.makeSymbolPositionFromSymbol(sym));
			if (respinSymbols.length >= symsInScatter && currentScatter < currentScatters.scattersPositions.length - 1) {
				const scatter: number = currentScatters.scattersPositions[currentScatter];
				const scatterPos: SymbolPosition = this.makeSymbolPositionFromSymbol(scatter);
				this._respinScatters.push(new RespinScatterInfo(scatterPos, respinSymbols));
				currentScatter++;
				respinSymbols = [];
			}
		}

		const scatter: number = currentScatters.scattersPositions[currentScatter];
		const scatterPos: SymbolPosition = this.makeSymbolPositionFromSymbol(scatter);
		this._respinScatters.push(new RespinScatterInfo(scatterPos, respinSymbols));
	}

	public setWins(wins: Win[]): void {
		this._winLines = [];

		const expandingWilds: ExpandingWildReel[] = [];

		if (this.expandingWilds && this.expandingWilds.length > 0) {
			for (let reel = 0; reel < ReelConstants.REELS_AMOUNT; reel++) {
				expandingWilds[reel] = null;
			}
		}

		for (const win of wins) {
			const positions: SymbolPosition[] = [];

			for (const pos of win.winSymbols) {
				const symbolPos = {
					x: pos % ReelConstants.REELS_AMOUNT,
					y: Math.trunc(pos / ReelConstants.REELS_AMOUNT),
				};

				if (!expandingWilds[symbolPos.x] && this.expandingWilds) {
					// TODO: Add exp wild to winline.
				}

				positions.push(symbolPos);
			}

			this._winLines.push(
				new GameWinLine(
					win.payline,
					this.convertPaytableReasonToSymID(win.reason),
					positions,
					positions,
					win.amount,
					win.ways,
					this.convertPaytableReasonToSymbolCount(win.reason),
				),
			);
		}
	}

	public setRespinNewSymbols(currentRoundSymbols: number[], currentScatters: ScatterReplacements, currentSymbolHeights: number[]): void {
		this._respinNewSymbols = [];

		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			// getting all the removed symbols indexes from scatters list
			const symbolsFromReel: SymbolPosition[] = this.getSymbolsPositionsFromReel(
				[...currentScatters.symbolsPositions, ...currentScatters.scattersPositions],
				reelIndex,
			);
			// cycling through that list to add the same amount of new symbols from the new reels
			for (let reelSymIndex = 0; reelSymIndex < symbolsFromReel.length; reelSymIndex++) {
				const symIndex: number = this.getRecodeSymbolIndexFromPosition({x: reelIndex, y: reelSymIndex});
				const gameSymbol: GameSymbol = this.makeGameSymbolFrom(currentRoundSymbols[symIndex], currentSymbolHeights[symIndex], {
					x: reelIndex,
					y: symIndex,
				});
				const newSymbol: AvalancheNewSymbol = new AvalancheNewSymbol(
					reelIndex,
					reelSymIndex,
					gameSymbol.sym,
					gameSymbol.size.height,
				);
				this._respinNewSymbols.push(newSymbol);
			}
		}
	}

	public setAvalancheSymbolsToRemove(symbolsToRemove: number[], symbols: number[]): void {
		this._symbolsToRemove = [];
		// for(const sym of symbolsToRemove) {
		for (let index = 0; index < symbolsToRemove.length; index++) {
			const sym = symbolsToRemove[index];
			if (!this.isExpandedWild(symbols[sym])) {
				this._symbolsToRemove.push(this.makeSymbolPositionFromSymbol(sym));
			}
		}
	}

	public setAvalancheNewSymbols(
		previousRoundSymbols: number[],
		currentRoundSymbols: number[],
		symbolsToRemove: number[],
		previousRoundSymbolHeight: number[],
		currentRoundSymbolsHeight: number[],
	): void {
		this._symbolsToAdd = [];
		this._expandedWildsToRemove = [];

		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			// getting all the removed symbols indexes from list
			const symbolsFromReel: SymbolPosition[] = this.getSymbolsPositionsFromReel(symbolsToRemove, reelIndex);
			let reelHasExpandingWild: boolean = false;

			const symbolsFromReelWithoutNulls = symbolsFromReel.filter((item) => {
				return previousRoundSymbols[this.getRecodeSymbolIndexFromPosition(item)] !== 7;
			});

			if (symbolsFromReelWithoutNulls.length === 1 && this.reelHasExpandedWild(previousRoundSymbols, reelIndex)) {
				this._expandedWildsToRemove.push(reelIndex);
				reelHasExpandingWild = true;
			}

			const reelInitialHeight: number = this.getSymbolsHeight(
				this.getSymbolsFromReel(currentRoundSymbols, reelIndex),
				this.getSymbolsFromReel(currentRoundSymbolsHeight, reelIndex),
			);

			let reelSize = reelInitialHeight;

			if (reelHasExpandingWild) {
				reelSize = 0;
			} else {
				for (const sym of symbolsFromReel) {
					const symIndex: number = this.getRecodeSymbolIndexFromPosition(sym);
					const gameSymbol: GameSymbol = this.makeGameSymbolFrom(
						previousRoundSymbols[symIndex],
						previousRoundSymbolHeight[symIndex],
						{x: sym.x, y: sym.y},
					);
					reelSize -= gameSymbol.size.height;
				}
			}

			let reelSymIndex: number = 0;
			while (reelSize < reelInitialHeight) {
				const symIndex: number = this.getRecodeSymbolIndexFromPosition({x: reelIndex, y: reelSymIndex});
				const gameSymbol: GameSymbol = this.makeGameSymbolFrom(currentRoundSymbols[symIndex], currentRoundSymbolsHeight[symIndex], {
					x: reelIndex,
					y: reelSymIndex,
				});
				if (gameSymbol) {
					const newSymbol: AvalancheNewSymbol = new AvalancheNewSymbol(
						reelIndex,
						reelSymIndex,
						gameSymbol.sym,
						gameSymbol.size.height,
					);
					this._symbolsToAdd.push(newSymbol);
					// reelSymIndex++;
					reelSize += gameSymbol.size.height;
				}
				// TODO: How shit does this smell?
				reelSymIndex++;
			}
		}
	}

	public setCurrentAvalancheMultiplier(multiplier: number): void {
		this._multiplier = multiplier;
	}

	public setBonusSymbolsList(...data: any[]): void {
		return;
	}

	public removeExpandedWild(reelIndex: number): void {
		let indexToRemove: number = -1;
		for (let i = 0; i < this.expandingWilds.length; i++) {
			if (this.expandingWilds[i].reelIndex === reelIndex) {
				indexToRemove = i;
			}
		}

		if (indexToRemove >= 0) {
			this.expandingWilds.splice(indexToRemove, 1);
		}
	}

	protected parseBalance(data: IRecodeSpinResponse): void {
		const balance = data.Response.Player.attributes.balance;
		this._balance = parseFloat(balance);
	}

	/**
	 * creates game symbol with height from recode symbol
	 */
	protected makeGameSymbolFrom(symbol: number, symbolHeight: number, position: SymbolPosition): GameSymbol {
		throw Error('RecodeSpinResult::makeGameSymbolFrom must be game specific');
	}

	protected convertPaytableReasonToSymbolCount(reason: string): number {
		return Number.parseInt(reason.substr(0, 1), 10);
	}

	protected convertPaytableReasonToSymID(reason: string): number {
		throw new Error('RecodeSpinResult::convertPaytableReasonToSymID must be game specific');
	}

	protected isAnExpandingWildOnReel(reel: number): boolean {
		for (const ew in this.expandingWilds) {
			if (this.expandingWilds.hasOwnProperty(ew)) {
				const element = this.expandingWilds[ew];
				if (element.reelIndex === reel) {
					return true;
				}
			}
		}

		return false;
	}

	/**
	 * creates SymbolPosition from recode symbol position
	 * @param symbol
	 */
	protected makeSymbolPositionFromSymbol(symbol: number): SymbolPosition {
		const reelPos = symbol % ReelConstants.REELS_AMOUNT;
		const rowPos = Math.trunc(symbol / ReelConstants.REELS_AMOUNT);

		return {x: reelPos, y: rowPos};
	}

	/**
	 * get all the symbols positions from specified reel from recode reels
	 * @param allSymbols
	 * @param reelIndex
	 */
	protected getSymbolsPositionsFromReel(allSymbols: number[], reelIndex: number): SymbolPosition[] {
		const reelSymbols: SymbolPosition[] = [];

		for (const sym of allSymbols) {
			const symPos: SymbolPosition = this.makeSymbolPositionFromSymbol(sym);
			if (symPos.x === reelIndex) {
				reelSymbols.push(symPos);
			}
		}

		return reelSymbols;
	}

	protected getSymbolsFromReel(allSymbols: number[], reelIndex: number): number[] {
		const reelSymbols: number[] = [];

		for (let i = 0; i < allSymbols.length; i++) {
			const symPos: SymbolPosition = this.makeSymbolPositionFromSymbol(i);
			if (symPos.x === reelIndex) {
				reelSymbols.push(allSymbols[i]);
			}
		}

		return reelSymbols;
	}

	/**
	 * makes recode symbol position from game symbol position
	 * @param pos
	 */
	protected getRecodeSymbolIndexFromPosition(pos: SymbolPosition): number {
		return pos.y * ReelConstants.REELS_AMOUNT + pos.x;
	}

	protected createReelsFromSymbolsList(symbols: number[], heights: number[]): GameSymbol[][] {
		const gameSymbols: GameSymbol[][] = [];
		for (let reel = 0; reel < ReelConstants.REELS_AMOUNT; reel++) {
			gameSymbols[reel] = [];
		}

		let symbolIndex: number = 0;
		let reelIndex: number = 0;

		for (let index = 0; index < symbols.length; index++) {
			const symbol = symbols[index];
			const height = heights[index];

			const gameSymbol: GameSymbol = this.makeGameSymbolFrom(symbol, height, {
				x: reelIndex,
				y: index % reelIndex,
			});
			if (gameSymbol) {
				gameSymbols[reelIndex].push(gameSymbol);
			}
			reelIndex++;
			if (reelIndex > ReelConstants.REELS_AMOUNT - 1) {
				symbolIndex++;
				reelIndex = 0;
			}
		}

		return gameSymbols;
	}

	protected isExpandedWild(symbol: number): boolean {
		// symbol = symbol > 50 ? 50 : symbol;

		switch (symbol) {
			case 0:
			case 7:
				// case 50:
				return true;
				break;
			default:
				return false;
				break;
		}
	}

	protected getSymbolsHeight(symbols: number[], symbolsHeights: number[]): number {
		let totalHeight: number = 0;

		// for (const sym of symbols) {
		for (let index = 0; index < symbols.length; index++) {
			const sym = symbols[index];
			const symheight = symbolsHeights[index];

			const gameSymbol: GameSymbol = this.makeGameSymbolFrom(sym, symheight, {x: 0, y: 0});
			if (gameSymbol) {
				totalHeight += gameSymbol.size.height;
			}
		}

		return totalHeight;
	}

	protected reelHasNullSymbol(symbolsFromReel: number[]): boolean {
		for (const sym of symbolsFromReel) {
			if (sym === 7) {
				return true;
			}
		}

		return false;
	}

	protected reelHasExpandedWild(currentRoundSymbols: number[], reelIndex: number): boolean {
		const symbolsFromReel: number[] = this.getSymbolsFromReel(currentRoundSymbols, reelIndex);
		let expandedWildsFound: number = 0;
		let reelHasNullSymbol: boolean = false;

		for (const sym of symbolsFromReel) {
			if (sym === 0) {
				expandedWildsFound++;
			}

			if (sym > 50) {
				reelHasNullSymbol = this.reelHasNullSymbol(symbolsFromReel);
				if (reelHasNullSymbol) {
					expandedWildsFound++;
				}
			}
		}
		if (expandedWildsFound > 1) {
			throw new Error('RecodeSpinResult::reelHasExpandedWild wierd stuff on reel ' + reelIndex);
		}

		return expandedWildsFound === 1;
	}
}

export interface IRecodeSpinResponse {
	Id: IRecodeSpinResponseId;
	Response: IRecodeSpinResponseResponse;
}

interface IRecodeSpinResponseResponse {
	Game: IRecodeSpinResponseGame;
	Player: IRecodeSpinResponsePlayer;
	Bet: IRecodeSpinResponseBet;
	Slot: IRecodeSpinResponseSlot;
	Tokens: IRecodeSpinResponseTokens;
	InGameProgress: IRecodeSpinResponseInGameProgress;
}

interface IRecodeSpinResponseInGameProgress {
	Progress: IRecodeSpinResponseToken[];
}

interface IRecodeSpinResponseTokens {
	Token: IRecodeSpinResponseToken[];
}

interface IRecodeSpinResponseToken {
	attributes: IRecodeSpinResponseTokenAttributes;
}

interface IRecodeSpinResponseTokenAttributes {
	name: string;
	value: string;
}

interface IRecodeSpinResponseSlot {
	attributes: IRecodeSpinResponseSlotAttributes;
	ResultSets: IRecodeSpinResponseResultSets;
	Wins: IRecodeSpinResponseWins;
}

interface IRecodeSpinResponseWins {
	Win: IRecodeSpinResponseWin[];
}

interface IRecodeSpinResponseWin {
	attributes: IRecodeSpinResponseWinAttributes;
}

interface IRecodeSpinResponseWinAttributes {
	amount: string;
	payline: string;
	paytableId: string;
	winSymbols: string;
	round: string;
	ways: string;
	reason: string;
}

interface IRecodeSpinResponseResultSets {
	Result: IRecodeSpinResponseResult[];
}

interface IRecodeSpinResponseResult {
	attributes: IRecodeSpinResponseResultAttributes;
	Reels?: IRecodeSpinResponseReel[];
}

interface IRecodeSpinResponseReel {
	attributes: IRecodeSpinResponseReelAttributes;
}

interface IRecodeSpinResponseReelAttributes {
	set: string;
	reelPos: string;
	symbols: string;
}

interface IRecodeSpinResponseResultAttributes {
	name: string;
}

interface IRecodeSpinResponseSlotAttributes {
	win: string;
	stage: string;
	resultIndex: string;
	nextStage: string;
	multiplier: string;
	freeSpins: string;
	freeSpinTotalAwarded: string;
	freeSpinTotalWin: string;
	transTotalWin: string;
}

interface IRecodeSpinResponseBet {
	attributes: IRecodeSpinResponseBetAttributes;
}

interface IRecodeSpinResponseBetAttributes {
	chipSize: string;
	numChips: string;
	activePaylines: string;
}

interface IRecodeSpinResponsePlayer {
	attributes: IRecodeSpinResponsePlayerAttributes;
}

interface IRecodeSpinResponsePlayerAttributes {
	balance: string;
	loyalty: string;
	userID: string;
	transNumber: string;
	totalWin: string;
	type: string;
	currency: string;
}

interface IRecodeSpinResponseGame {
	attributes: IRecodeSpinResponseGameAttributes;
}

interface IRecodeSpinResponseGameAttributes {
	state: string;
	version: string;
}

interface IRecodeSpinResponseId {
	attributes: IRecodeSpinResponseIdAttributes;
}

interface IRecodeSpinResponseIdAttributes {
	mid: string;
	cid: string;
	sid: string;
	sessionid: string;
	verb: string;
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/logic/integration/RecodeSpinResult.ts